import { RefObject } from 'react'
import styled from 'styled-components'
import { UiWallet } from '@mysten/dapp-kit-react'

import { IconLink } from 'components/Icons/IconLink'
import { IconRight } from 'components/Icons/IconRight'
import { Menu, MenuItem } from 'components/Menu/Menu'
import { Typography } from 'components/Typography/Typography'
import { getWalletUniqueIdentifier } from 'utils/wallet'
import { useViewAsWallet } from 'contexts/ViewAsWalletContext'

const ConnectWalletMenuContainer = styled.div`
  position: relative;
`

const ConnectButton = styled.button<{ $disabled: boolean }>`
  cursor: pointer;
  background-color: ${p => p.theme.colors.button.background};
  color: ${p => p.theme.colors.text.primaryDarkBackground};
  border: none;
  padding: 12px 24px;
  border-radius: 9999px;
  font-size: ${p => p.theme.typography.size.Description}px;
  font-family: ${p => p.theme.typography.family.Poppins};
  font-weight: ${p => p.theme.typography.weight.Medium};
  display: flex;
  align-items: center;
  & > svg {
    margin-left: 8px;
  }

  transition: background-color 0.3s ease;
  &:hover {
    ${p => `background-color: ${p.theme.colors.button.hover};`}
  }

  @media screen and (max-width: ${p => p.theme.breakpoints.md}px) {
    font-size: ${p => p.theme.typography.size.Caption1}px;
    padding: 8px 16px;
  }

  ${p => p.$disabled && `opacity: 0.3; pointer-events: none;`}
`

const WalletIcon = styled.img`
  width: 24px;
  height: 24px;
  margin-right: 8px;
`

const Divider = styled.div`
  margin: 0 16px;
  border-top: 1px solid ${p => p.theme.colors.divider};
`

const Description = styled(Typography)`
  padding: 16px;
  color: ${p => p.theme.colors.text.description};
`

interface Props {
  toggleMenu: () => void
  isMenuOpen: boolean
  menuNode: RefObject<HTMLDivElement | null>
  wallets: UiWallet[]
  menuStyles?: React.CSSProperties
  menuClass?: string
  onWalletSelect: (wallet: UiWallet) => void
}
export const ConnectWalletMenu = ({
  toggleMenu,
  isMenuOpen,
  menuNode,
  wallets,
  menuStyles,
  menuClass,
  onWalletSelect,
}: Props) => {
  const { viewAsWalletAddress } = useViewAsWallet()

  return (
    <ConnectWalletMenuContainer>
      <ConnectButton onClick={toggleMenu} $disabled={!!viewAsWalletAddress}>
        Connect wallet
        <IconLink />
      </ConnectButton>
      {isMenuOpen && (
        <Menu anchorElement={menuNode} style={menuStyles} className={menuClass}>
          <Typography variant="label2" fontWeight="semiBold" style={{ padding: 16 }}>
            Connect a Wallet
          </Typography>
          <Divider />
          {wallets.map(w => (
            <MenuItem onClick={() => onWalletSelect(w)} key={getWalletUniqueIdentifier(w)}>
              <Typography
                variant="description"
                fontWeight="light"
                style={{ display: 'flex', alignItems: 'center', width: '100%' }}
              >
                {typeof w.icon === 'string' ? <WalletIcon src={w.icon} alt={`${w.name} logo`} /> : w.icon}
                {w.name}
                <IconRight style={{ marginLeft: 'auto' }} />
              </Typography>
            </MenuItem>
          ))}
          <Divider />
          <Description variant="body" fontWeight="regular">
            Had some trouble while connecting? Follow our{' '}
            <span style={{ fontWeight: 700, cursor: 'pointer' }}>step-by-step</span> instructions to avoid mistakes!
          </Description>
        </Menu>
      )}
    </ConnectWalletMenuContainer>
  )
}
